// Import react elements
import { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import Strich API calls
import { getImage } from "api/APIStrichCalls";

// Import components
import FilterSidebar from './FilterSidebar';
import ImageGrid from './ImageGrid';
import PreviewPane from './PreviewPane';
import UploadCard from './UploadCard';
import BulkActionBar from './BulkActionBar';
import Pagination from 'global/components/Pagination';

// Import hooks
import useImageData from '../hooks/useImageData';
import useResizablePane from '../hooks/useResizablePane';
import useIsMobile from "global/hooks/useIsMobile";
import { useToast } from "global/hooks/useToast";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";

// Import css
import './ImageManager.css';



// Parent container component for the full image management interface
const ImageManager = ({setSelectedBucketId, view = "page", selectionMode = null}) => {

    // Normal Image Bucket pages allow multiple selection while popup pickers default to one image.
    const resolvedSelectionMode = selectionMode || (view === "popup" ? "single" : "multiple");

    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Get the toast helper used when associated bucket files cannot be loaded.
    const { toastError } = useToast();

    // Set isMobile from use hook 
    const isMobile = useIsMobile();

    // State for selected image to show in preview pane
    const [selectedImage, setSelectedImage] = useState(null);

    // State for filter selections
    const [typeFilter, setTypeFilter] = useState('All');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [subcategoryFilter, setSubcategoryFilter] = useState('All');

    // State for selected image IDs (for bulk actions)
    const [selectedImages, setSelectedImages] = useState([]);
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

    // State to control thumbnail size
    const [thumbnailSize, setThumbnailSize] = useState('m');

    // State for sort option
    const [sortOption, setSortOption] = useState('newest');

    // Add a state for holding the search query text
    const [searchQuery, setSearchQuery] = useState('');

    // Store each fully hydrated bucket item as the user drills into associated files.
    const [bucketNavigationStack, setBucketNavigationStack] = useState([]);

    // Track the bucket item currently loading its children so it cannot be opened twice.
    const [loadingBucketId, setLoadingBucketId] = useState(null);

    // Track the bucket item currently being moved so another relationship change cannot start at the same time.
    const [movingBucketItemId, setMovingBucketItemId] = useState(null);

    // Track external file dragging over the main Image Bucket.
    const [isUploadDragOver, setIsUploadDragOver] = useState(false);
    const [draggedUploadCount, setDraggedUploadCount] = useState(0);
    const uploadDragDepthRef = useRef(0);


    // Custom hook for fetching and managing image data
    const {
        imageData,
        filteredImages,
        loadMoreImages,
        isLoading,
        updateSelectedImage,
        deleteImages,
        addImages,
        refreshImages,
        filters,
        isLoadingWhileAddingImages,
        uploadProgress
    } = useImageData(typeFilter, categoryFilter, subcategoryFilter, sortOption, searchQuery, setSelectedImages, setSelectedImages); 


    // Get the fully hydrated bucket item for the current associated-file level.
    const activeBucketImage = bucketNavigationStack.length > 0 ? bucketNavigationStack[bucketNavigationStack.length - 1] : null;


    // Build the image/file list for the current bucket level.
    const visibleImages = useMemo(() => {

        // Use the normal filtered top-level list until the user drills into a bucket item.
        if (!activeBucketImage) return filteredImages;

        // Copy the children because sorting should not mutate the hydrated API response.
        let childImages = [...activeBucketImage.children];

        // Apply the filename search to associated child files as well.
        if (searchQuery) {

            childImages = childImages.filter((image) => image.name.toLowerCase().includes(searchQuery.toLowerCase()));

        }

        // Keep the current parent first while sorting its associated children with the selected sort option.
        if (sortOption === 'newest') {

            childImages.sort((a, b) => b.id - a.id);

        } else if (sortOption === 'oldest') {

            childImages.sort((a, b) => a.id - b.id);

        } else if (sortOption === 'filename-asc') {

            childImages.sort((a, b) => a.name.localeCompare(b.name));

        } else if (sortOption === 'filename-desc') {

            childImages.sort((a, b) => b.name.localeCompare(a.name));

        }

        // Include the primary parent file before its associated children.
        return [activeBucketImage, ...childImages];

    }, [activeBucketImage, filteredImages, searchQuery, sortOption]);


    // Custom hook to handle resizing preview pane
    const { paneSize: previewPaneWidth, resizerRef: rightResizerRef } = useResizablePane(220, 'right', 'horizontal');
    const { paneSize: filterPaneWidth, resizerRef: leftResizerRef } = useResizablePane(220, 'left', 'horizontal');

    const { paneSize: previewPaneHeight, resizerRef: mobilePreviewPaneResizerRef } = useResizablePane(200, 'top', 'vertical');
    const { paneSize: filterPaneHeight, resizerRef: mobileFilterPaneResizerRef } = useResizablePane(200, 'bottom', 'vertical');



    // Callback to select/deselect images (for Ctrl+click and Shift+click)
    const handleImageSelect = useCallback((id, index, event) => {

        // Single-selection pickers must never build a multi-selection
        if (resolvedSelectionMode === "single") {

            setSelectedImages((previousSelection) => {

                // Clicking the only selected image deselects it
                if (previousSelection.length === 1 && previousSelection[0] === id) {

                    setSelectedImage(null);
                    return [];

                }

                // Find the selected image in the current bucket level
                const image = visibleImages.find((imageItem) => imageItem.id === id);

                // Update the preview pane
                setSelectedImage(image || null);

                // Keep exactly one selected image
                return [id];

            });

            // Keep the selected index current
            setLastSelectedIndex(index);

            return;

        }


        // Shift-click range select
        // If the user is holding Shift and there was a previous selection
        if (event.shiftKey && lastSelectedIndex !== null) {

            // Determine the range between the last selected index and the current clicked index
            const [start, end] = [lastSelectedIndex, index].sort((a, b) => a - b);

            // Extract the image IDs within the selected range
            const rangeIds = visibleImages.slice(start, end + 1).map(img => img.id);

            // Add the range to the current selection, ensuring no duplicates
            setSelectedImages(prev => Array.from(new Set([...prev, ...rangeIds])));

            // Exit early — skip normal selection logic since Shift+Click was handled
            return;

        }


        // Ctrl/Cmd-click toggles individual images within the current selection
        if (event.ctrlKey || event.metaKey) {

            setSelectedImages(prev => {

                // Toggle selection: remove if already selected, add if not
                const updated = prev.includes(id)

                    // deselect
                    ? prev.filter(i => i !== id)

                    // Select
                    : [...prev, id];

                return updated;

            });

            // Update last selected index for future Shift+Click range logic
            setLastSelectedIndex(index);

            // Exit early — skip normal single-select logic
            return;

        }


        // Regular click: Update the selectedImages array when an image is clicked
        setSelectedImages(prev => {

            // Create boolean if the clicked image is already the only one selected
            const isDeselecting = prev.length === 1 && prev[0] === id;

            // If deselecting
            if (isDeselecting) {

                // Clear the selectedImage preview pane
                setSelectedImage(null);

                // Return empty array
                return [];

            }

            // Otherwise, select the clicked image - find the clicked image in the visible bucket level
            const image = visibleImages.find(img => img.id === id);

            // Set the selectedImage to it
            setSelectedImage(image);

            // Replace any existing selection with this one
            return [id];

        });


        // Record the index for shift+click range selection
        setLastSelectedIndex(index);

    // Re run when dependancies change
    }, [visibleImages, lastSelectedIndex, resolvedSelectionMode, setSelectedImage]);



    useEffect(() => {

        // Check if the selectedImages has only 1 image
        if (selectedImages.length === 1) {

            // Find the image within the currently visible bucket level
            const image = visibleImages.find(img => img.id === selectedImages[0]);

            // Set it as the selectedImage
            setSelectedImage(image || null);

        } else {

            // Otherwise set selectedImage null because there's none or multiple selected
            setSelectedImage(null);

        }

        // Re run when selectedImages or the visible bucket level changes
    }, [selectedImages, visibleImages]);

    // Return the current bucket selection to picker callers.
    useEffect(() => {

        // There is no picker callback on the normal Image Bucket page
        if (!setSelectedBucketId) return;

        // Single-selection pickers return one image object or null
        if (resolvedSelectionMode === "single") {

            if (selectedImages.length !== 1) {
                setSelectedBucketId(null);
                return;
            }

            const selectedBucketImage = visibleImages.find((image) => image.id === selectedImages[0]);
            setSelectedBucketId(selectedBucketImage || null);

            return;

        }

        // Multiple-selection pickers return all selected image objects
        const selectedBucketImages = selectedImages
            .map((imageId) => visibleImages.find((image) => image.id === imageId))
            .filter(Boolean);

        setSelectedBucketId(selectedBucketImages);

    }, [resolvedSelectionMode, selectedImages, setSelectedBucketId, visibleImages]);

    // Callback to deselect all
    const clearSelection = useCallback(() => {

        // Clear selected images array
        setSelectedImages([]);

        // Clear last selected index
        setLastSelectedIndex(null);

    }, []);


    // Check whether this drag contains files from outside the portal.
    const isExternalFileDrag = useCallback((event) => {

        // Get the drag data types supplied by the browser
        const dragTypes = Array.from(event.dataTransfer?.types || []);

        // External file drags include the Files data type
        return dragTypes.includes("Files");

    }, []);



    // Handle external files entering the Image Bucket.
    const handleUploadDragEnter = useCallback((event) => {

        // Only enable whole-manager uploads on the normal root Image Bucket page
        if (view !== "page" || activeBucketImage || isLoadingWhileAddingImages) return;
        if (!isExternalFileDrag(event)) return;

        event.preventDefault();
        event.stopPropagation();

        // Keep a depth count so moving across child elements does not hide the overlay
        uploadDragDepthRef.current += 1;

        // Count the files being dragged where the browser exposes them
        const draggedItems = Array.from(event.dataTransfer?.items || []);
        const fileItems = draggedItems.filter((item) => item.kind === "file");

        let fileCount = fileItems.length;
        if (fileCount === 0) { fileCount = 1; }

        setDraggedUploadCount(fileCount);
        setIsUploadDragOver(true);

    }, [activeBucketImage, isExternalFileDrag, isLoadingWhileAddingImages, view]);



    // Keep external file dropping enabled while moving over the Image Bucket.
    const handleUploadDragOver = useCallback((event) => {

        // Only handle external files on the normal root Image Bucket page
        if (view !== "page" || activeBucketImage || isLoadingWhileAddingImages) return;
        if (!isExternalFileDrag(event)) return;

        event.preventDefault();
        event.stopPropagation();

        // Tell the browser this is a copy operation rather than moving the source file
        event.dataTransfer.dropEffect = "copy";

    }, [activeBucketImage, isExternalFileDrag, isLoadingWhileAddingImages, view]);



    // Hide the upload overlay when the external drag leaves the Image Bucket.
    const handleUploadDragLeave = useCallback((event) => {

        // Ignore normal internal bucket-image drags
        if (!isExternalFileDrag(event)) return;

        event.preventDefault();
        event.stopPropagation();

        // Reduce the child-element drag depth
        uploadDragDepthRef.current -= 1;

        if (uploadDragDepthRef.current > 0) return;

        // The external drag has completely left the manager
        uploadDragDepthRef.current = 0;
        setIsUploadDragOver(false);
        setDraggedUploadCount(0);

    }, [isExternalFileDrag]);



    // Upload files dropped anywhere over the main Image Bucket.
    const handleUploadDrop = useCallback(async (event) => {

        // Only intercept external files on the normal root Image Bucket page
        if (view !== "page" || activeBucketImage) return;
        if (!isExternalFileDrag(event)) return;

        event.preventDefault();
        event.stopPropagation();

        // Clear the drag overlay immediately
        uploadDragDepthRef.current = 0;
        setIsUploadDragOver(false);
        setDraggedUploadCount(0);

        // Do not start another upload group while one is already running
        if (isLoadingWhileAddingImages) return;

        // Get all files from the drop
        const files = Array.from(event.dataTransfer.files || []);
        if (files.length === 0) return;

        // Clear the current bucket selection before adding new images
        clearSelection();

        // Build the same upload objects used by the existing upload card
        const imagesToUpload = files.map((file) => {

            return {
                name: file.name.split(".").slice(0, -1).join("."),
                file
            };

        });

        try {

            // Upload the complete dropped file group
            await addImages(imagesToUpload);

        } catch (error) {

            console.error("Could not upload dropped images:", error);

            toastError({
                title: "Error",
                message: "Could not upload the dropped images.",
                autoCloseMs: 6000
            });

        }

    }, [activeBucketImage, addImages, clearSelection, isExternalFileDrag, isLoadingWhileAddingImages, toastError, view]);


    // Rehydrate the currently open bucket parent and its associated children.
    const refreshActiveBucketLevel = useCallback(async () => {

        // There is nothing to hydrate while browsing the normal top-level bucket.
        if (!activeBucketImage) return null;

        try {

            // Fetch the complete parent again so child changes come from current API data.
            const response = await getImage(token, activeBucketImage.id);

            // Surface API errors instead of leaving the current level silently stale.
            if (!response?.success) {

                toastError({ title: "Error", message: "Could not refresh associated files: ", apiErrorMessage: response?.error_message, autoCloseMs: 6000 });

                return null;

            }

            // The parent must still exist after the mutation.
            if (!Array.isArray(response.results) || response.results.length === 0) {

                toastError({ title: "Error", message: "Could not refresh the selected bucket item.", autoCloseMs: 6000 });

                return null;

            }

            // Get the newly hydrated parent.
            const refreshedParent = response.results[0];

            // Child details are required for an associated-files level.
            if (!Array.isArray(refreshedParent.children)) {

                toastError({ title: "Error", message: "The selected bucket item did not include its associated file details.", autoCloseMs: 6000 });

                return null;

            }

            // Replace only the current level while preserving any previous navigation levels.
            setBucketNavigationStack((previousStack) => {

                // Stop if the user has already left the associated-files view.
                if (previousStack.length === 0) return previousStack;

                // Copy the stack so React receives a new state object.
                const updatedStack = [...previousStack];

                // Replace the current hydrated parent with the latest API version.
                updatedStack[updatedStack.length - 1] = refreshedParent;

                // Return the refreshed navigation stack.
                return updatedStack;

            });

            // Let callers use the refreshed record if needed.
            return refreshedParent;

        } catch (error) {

            console.error("Could not refresh associated bucket files:", error);

            // Surface unexpected refresh errors.
            toastError({ title: "Error", message: "Could not refresh associated files.", autoCloseMs: 6000 });

            return null;

        }

    }, [activeBucketImage, toastError, token]);



    // Update an image and refresh the current associated-files level when necessary.
    const handleUpdateSelectedImage = useCallback(async (id, updatedImageData) => {

        // Perform the normal image update first.
        const response = await updateSelectedImage(id, updatedImageData);

        // Do not refresh local hierarchy data after a failed update.
        if (!response?.success) return response;

        // Rehydrate the parent when this update happened inside an associated-files level.
        if (activeBucketImage) await refreshActiveBucketLevel();

        // Return the original update response to the calling component.
        return response;

    }, [activeBucketImage, refreshActiveBucketLevel, updateSelectedImage]);



    // Move a bucket item under another root item, or detach it back to the bucket root.
    const handleMoveBucketItem = useCallback(async (imageId, parentId) => {

        // Do not start another relationship update while one is already running.
        if (movingBucketItemId !== null) return;

        // Mark this item as moving until the API and bucket refresh have finished.
        setMovingBucketItemId(imageId);

        try {

            // Parent id is handled by the normal image update endpoint.
            const response = await handleUpdateSelectedImage(imageId, { parent_id: parentId });

            // Surface API validation failures such as invalid hierarchy changes.
            if (!response?.success) {

                toastError({ title: "Error", message: "Could not move bucket item: ", apiErrorMessage: response?.error_message, autoCloseMs: 6000 });

                return;

            }

            // Clear selection because the moved item may no longer exist in the current level.
            clearSelection();

            // Refresh the root bucket so moved items and child counts are immediately current.
            await refreshImages();

        } catch (error) {

            console.error("Could not move bucket item:", error);
            toastError({ title: "Error", message: "Could not move bucket item.", autoCloseMs: 6000 });

        } finally {

            // Re-enable relationship changes after the move attempt finishes.
            setMovingBucketItemId(null);

        }

    }, [clearSelection, handleUpdateSelectedImage, movingBucketItemId, refreshImages, toastError]);



    // Build a simple snapshot so we can detect when the bucket changes after Customizr closes.
    const getImageRefreshSignature = useCallback((images) => {

        return JSON.stringify((images || []).map((item) => `${item.id}:${item.image_url || ""}`));

    }, []);



    // Refresh the bucket in place instead of forcing a full page reload.
    const handleCustomizrRefresh = useCallback(async () => {

        // Save what the current bucket looks like before refreshing.
        const previousSignature = getImageRefreshSignature(imageData);

        // Match the old full-reload behaviour by clearing selection first.
        clearSelection();

        // Try a few times because the saved image can take a moment to appear in the bucket.
        for (let attempt = 0; attempt < 5; attempt += 1) {

            // Fetch the latest bucket contents.
            const refreshedImages = await refreshImages();

            // Stop retrying once the image list actually changed.
            if (getImageRefreshSignature(refreshedImages) !== previousSignature) {
                return;
            }

            // Wait briefly before trying again.
            await new Promise((resolve) => {
                setTimeout(resolve, 500);
            });
        }

    }, [clearSelection, getImageRefreshSignature, imageData, refreshImages]);



    // Handlers to set 'Type' filter and reset dependent filters
    const handleSetTypeFilter = (value) => {

        // Set the type
        setTypeFilter(value);

        // Clear category by setting to 'all'
        setCategoryFilter('All');

        // Clear subcategory by setting to 'all'
        setSubcategoryFilter('All');

    };



    // Handler to set 'Category' filter and reset dependent
    const handleSetCategoryFilter = (value) => {

        // Set the category
        setCategoryFilter(value);

        // Clear subcategory by setting to 'all'
        setSubcategoryFilter('All');

    };



    // Handler to delete images
    const handleDelete = async (imageIDs) => {

        // Perform the actual delete once confirmation requirements have been satisfied
        const performDelete = async () => {

            // Perform deletion using the IDs actually passed to this handler
            const failedIds = await deleteImages(imageIDs);

            // Build a simple list of IDs that failed
            const failedIdValues = failedIds.map((failedDelete) => failedDelete.id);

            // Work out which requested records were successfully deleted
            const successfulIds = imageIDs.filter((imageId) => !failedIdValues.includes(imageId));

            // Check whether the currently open parent itself was successfully deleted
            let activeParentDeleted = false;

            // Only compare against a parent while an associated-files level is open
            if (activeBucketImage) {
                activeParentDeleted = successfulIds.includes(activeBucketImage.id);
            }

            // Leave the deleted parent's level because it can no longer be hydrated
            if (activeParentDeleted) {

                // Clear selection because the current level is disappearing
                clearSelection();

                // Return to the previous bucket level
                setBucketNavigationStack((previousStack) => previousStack.slice(0, -1));

                // Start the previous level back on its first page
                setCurrentPage(1);

                return;

            }

            // Rehydrate the current parent when one or more children were successfully deleted
            if (activeBucketImage && successfulIds.length > 0) {

                await refreshActiveBucketLevel();

                // Refresh the root bucket list so the parent's child count is current when returning to the top level
                await refreshImages();

            }

            // Keep only failed deletions selected so the user can retry them
            setSelectedImages(failedIdValues);

        };

        let confirmationMessage = "Are you sure you want to delete this image? This cannot be undone.";

        if (imageIDs.length > 1) {
            confirmationMessage = "You're about to delete " + imageIDs.length + " images. Are you sure?";
        }

        // Show confirmation dialog
        DisplayConfirmationDialog(

            confirmationMessage,
            "Yes",
            "Cancel",

            async function (is_confirmed) {

                // Return early if cancel is clicked
                if (!is_confirmed) return;

                // Perform the confirmed deletion
                await performDelete();

            }

        );

    };


    // PAGINATION

    // Set state for current page
    const [currentPage, setCurrentPage] = useState(1);

    // Set state for rows per page
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // Effective visual page size (images per page when reserving a slot)
    const visualPageSize = rowsPerPage - 1;

    // Total items in the currently visible bucket level
    const totalImages = visibleImages.length;

    // Calculate the number of required pages (filling the reserved slot correctly)
    const totalPages = Math.ceil(totalImages / visualPageSize);

    // Check if the user is on the last page
    const isLastPage = currentPage === totalPages;

    // Calculate the starting index
    const startIndex = (currentPage - 1) * visualPageSize;

    // Store a reference to the top of the table area
    const imageGridTopRef = useRef(null);


    // Calculate the ending index
    let endIndex;

    if (!isLastPage) {

        // If not on the last page, reserve one slot for 'See More Images'
        endIndex = startIndex + visualPageSize;

    } else {

        // On the last page, show all remaining images without reserving a slot
        endIndex = startIndex + rowsPerPage;
    }

    // Slice the currently visible bucket level for this page
    const paginatedImageData = visibleImages.slice(startIndex, endIndex);

    // Control whether to show the 'See More' slot - pass this to the imageGrid - if it's not the last page and there are more images than the current pagination value
    const showSeeMoreSlot = !isLastPage && (totalPages > 1);



    // Load the complete bucket item and drill into its associated child files.
    const handleOpenImageChildren = useCallback(async (image) => {

        // Stop if this item has no children or another child request is already running.
        if (!image || Number(image.child_count) <= 0 || loadingBucketId !== null) return;

        // Mark this item as loading so repeated clicks cannot add the same level twice.
        setLoadingBucketId(image.id);

        try {

            // Fetch the complete bucket record because the main image list intentionally excludes child details.
            const response = await getImage(token, image.id);

            // Surface API errors instead of entering an incomplete bucket level.
            if (!response?.success) {

                toastError({ title: "Error", message: "Could not load associated files: ", apiErrorMessage: response?.error_message, autoCloseMs: 6000 });

                return;

            }

            // A single image lookup must return one complete bucket record.
            if (!Array.isArray(response.results) || response.results.length === 0) {

                toastError({ title: "Error", message: "Could not load the selected bucket item.", autoCloseMs: 6000 });

                return;

            }

            const fullImage = response.results[0];

            // Child details must be hydrated by the single-image endpoint before we enter this level.
            if (!Array.isArray(fullImage.children)) {

                toastError({ title: "Error", message: "The selected bucket item did not include its associated file details.", autoCloseMs: 6000 });

                return;

            }

            // Clear selection before changing the visible bucket level.
            clearSelection();

            // Push the complete item onto the stack so nested child groups can be browsed later too.
            setBucketNavigationStack((previousStack) => [...previousStack, fullImage]);

            // Always start a newly opened level on its first page.
            setCurrentPage(1);

        } catch (error) {

            console.error("Could not load associated bucket files:", error);
            toastError({ title: "Error", message: "Could not load associated files.", autoCloseMs: 6000 });

        } finally {

            // Re-enable child navigation after the request finishes.
            setLoadingBucketId(null);

        }

    }, [clearSelection, loadingBucketId, toastError, token]);



    // Return to the previous bucket level.
    const handleBackImageLevel = useCallback(() => {

        // Clear selection because the selected item may not exist at the previous level.
        clearSelection();

        // Remove the current level from the navigation stack.
        setBucketNavigationStack((previousStack) => previousStack.slice(0, -1));

        // Return to the first page of the previous level.
        setCurrentPage(1);

    }, [clearSelection]);

    

    // Scroll the table back into view.
    const handleScrollToImageGridTop = () => {

        // Stop if the ref hasn't been created yet.
        if (!imageGridTopRef.current) return;

        // Wait until React has rendered the new page.
        requestAnimationFrame(() => {

            // Smoothly scroll the table into view.
            imageGridTopRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });

        });

    };
 
    // Build the upload overlay text
    let showUploadOverlay = false;
    let uploadOverlayTitle = "";
    let uploadOverlayStatus = "";

    // Show drop instructions while external files are over the Image Bucket
    if (isUploadDragOver) {

        showUploadOverlay = true;

        if (draggedUploadCount > 1) {
            uploadOverlayTitle = "Drop " + draggedUploadCount + " images to upload";
        } else {
            uploadOverlayTitle = "Drop image to upload";
        }

    }

    // Show live progress while files are being uploaded
    if (isLoadingWhileAddingImages) {

        showUploadOverlay = true;

        if (uploadProgress.completed >= uploadProgress.total && uploadProgress.total > 0) {

            uploadOverlayTitle = "Updating image bucket...";

        } else if (uploadProgress.total > 1) {

            uploadOverlayTitle = "Uploading images...";
            uploadOverlayStatus = uploadProgress.completed + " of " + uploadProgress.total + " complete";

        } else {

            uploadOverlayTitle = "Uploading image...";

        }

        // Include failure information immediately if an individual upload fails
        if (uploadProgress.failed > 0) {

            if (uploadOverlayStatus !== "") {
                uploadOverlayStatus += " - ";
            }

            uploadOverlayStatus += uploadProgress.failed + " failed";

        }

    }

    return (

        <>

            {/* Header bar with actions for single or bulk images selected */}
            <BulkActionBar
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                clearSelection={clearSelection}
                onDelete={() => handleDelete(selectedImages)}
                updateSelectedImage={updateSelectedImage}
                onUpdatesComplete={refreshActiveBucketLevel}
                setThumbnailSize={setThumbnailSize}
                sortOption={sortOption}
                setSortOption={setSortOption}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                imageData={imageData}
                setCurrentPage={setCurrentPage}
                filteredImages={visibleImages}  
            />


            <div
                className={`image-manager ${view === "popup" ? "image-manager-popup" : ""}`}
                onDragEnterCapture={handleUploadDragEnter}
                onDragOverCapture={handleUploadDragOver}
                onDragLeaveCapture={handleUploadDragLeave}
                onDropCapture={handleUploadDrop}
            >

                {/* Show an obvious whole-manager target while dragging or uploading external files. */}
                {view === "page" && showUploadOverlay &&

                    <div className="image-manager__upload-overlay">

                        <div className="image-manager__upload-overlay-content">

                            {isLoadingWhileAddingImages &&
                                <div className="image-manager__upload-spinner" />
                            }

                            <strong>{uploadOverlayTitle}</strong>

                            {uploadOverlayStatus !== "" &&
                                <span>{uploadOverlayStatus}</span>
                            }

                        </div>

                    </div>

                }

                <div className="filter-sidebar filter-sidebar--manager"
                    style={{
                        width: isMobile ? undefined : filterPaneWidth,
                        height: isMobile ? filterPaneHeight : undefined
                    }}
                >

                    <FilterSidebar
                        filters={filters}
                        currentFilters={{ typeFilter, categoryFilter, subcategoryFilter }}
                        setFilters={{
                            setTypeFilter: handleSetTypeFilter,
                            setCategoryFilter: handleSetCategoryFilter,
                            setSubcategoryFilter
                        }}
                        onDropUpdate={handleUpdateSelectedImage}
                        clearSelection={clearSelection}
                    />

                </div>


                {/* Draggable resizer bar - vertical on desktop and horizontal on mobile */}
                {isMobile ?

                    <div className="image-manager__resizer-mobile" ref={mobileFilterPaneResizerRef} />

                    :
            
                    <div className="image-manager__resizer" ref={leftResizerRef} />

                }


                {/* Center section with image grid and upload card */}
                <div className="image-manager__grid">

                    {activeBucketImage ?

                        <div className="image-manager__associated-files-header">
                            <span>Associated files for:</span>
                            <strong>{activeBucketImage.name}</strong>
                        </div>

                        :

                        <UploadCard addImages={addImages} isLoadingWhileAddingImages={isLoadingWhileAddingImages} setSelectedImages={setSelectedImages} />

                    }

                    {paginatedImageData.length === 0 ? 
                    
                        <p>No images to display.</p>

                    :
                
                        <ImageGrid
                            paginatedImageData={paginatedImageData}
                            isLoading={isLoading}
                            handleImageSelect={handleImageSelect}
                            selectedImages={selectedImages}
                            setSelectedImages={setSelectedImages}
                            loadMoreImages={loadMoreImages}
                            thumbnailSize={thumbnailSize}
                            showSeeMoreSlot={showSeeMoreSlot}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            imageGridTopRef={imageGridTopRef}
                            showBackCard={activeBucketImage !== null}
                            onBack={handleBackImageLevel}
                            onOpenChildren={handleOpenImageChildren}
                            loadingBucketId={loadingBucketId}
                            currentParentId={activeBucketImage?.id}
                            onMoveImage={handleMoveBucketItem}
                            movingBucketItemId={movingBucketItemId}
                            onDeleteImage={(imageId) => handleDelete([imageId])}
                        />

                    }

                </div>


                {/* Draggable resizer bar - vertical on desktop and horizontal on mobile */}
                {isMobile ?

                    <div className="image-manager__resizer-mobile" ref={mobilePreviewPaneResizerRef} />

                    :

                    <div className="image-manager__resizer" ref={rightResizerRef} />

                }

                {/* Right preview pane for selected image */}

                <div className="preview-pane-conatiner"
                    style={{
                        width: isMobile ? undefined : previewPaneWidth,
                        height: isMobile ? previewPaneHeight : undefined
                    }}
                >

                    <PreviewPane
                        image={selectedImage}
                        updateSelectedImage={handleUpdateSelectedImage}
                        onDelete={() => handleDelete([selectedImage.id])}
                        onCustomizrSave={handleCustomizrRefresh}
                    />

                </div>

            </div>

            <div>
                <div>
                    <Pagination
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        rowsPerPageOptions={[25, 50, 100, 500]}
                        setSelectedRows={setSelectedImages}
                        paginationDropdownMessage={activeBucketImage ? "Files" : "Images"}
                        onPageChangeScroll={handleScrollToImageGridTop}
                    />
                </div>
            </div>

        </>

    );

};

export default ImageManager;

